import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import type { MaintenancePlan, Paginated, Vehicle } from '@logistech/ui-shared';

const NO_PLAN = 'none';

// Aligné sur le schema Zod du maintenance-service (routes/work-orders/index.ts)
const schema = z.object({
  vehicleId: z.string().uuid('Véhicule requis'),
  planId: z.string(),
  description: z.string().trim().min(2, 'Décrire l’intervention').max(500),
  scheduledDate: z.string().min(1, 'Date requise'),
});

type FormData = z.infer<typeof schema>;

const today = () => new Date().toISOString().slice(0, 10);

const toDefaults = (fromPlan?: MaintenancePlan): FormData => ({
  vehicleId: fromPlan?.vehicleId ?? '',
  planId: fromPlan?.id ?? NO_PLAN,
  description: fromPlan?.name ?? '',
  scheduledDate: today(),
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pré-remplissage depuis un plan préventif (« Planifier maintenant ») */
  fromPlan?: MaintenancePlan;
}

/** Intervention ponctuelle datée → ordre de travail « Planifié ». */
export function WorkOrderDialog({ open, onOpenChange, fromPlan }: Props) {
  const qc = useQueryClient();
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } =
    useForm<FormData>({ resolver: zodResolver(schema), defaultValues: toDefaults(fromPlan) });

  useEffect(() => { if (open) reset(toDefaults(fromPlan)); }, [open, fromPlan, reset]);

  const vehicleId = watch('vehicleId');

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', 'active'],
    queryFn: async () => (await api.get<Paginated<Vehicle>>('/vehicles?pageSize=100')).data,
    enabled: open,
  });

  const { data: plans } = useQuery({
    queryKey: ['maintenance-plans', vehicleId],
    queryFn: async () =>
      (await api.get<Paginated<MaintenancePlan>>(`/maintenance/plans?vehicleId=${vehicleId}&limit=200`)).data,
    enabled: open && !!vehicleId,
  });

  const mutation = useMutation({
    mutationFn: (d: FormData) =>
      api.post('/work-orders', {
        vehicleId: d.vehicleId,
        planId: d.planId === NO_PLAN ? undefined : d.planId,
        description: d.description,
        // Midi local : la date choisie reste la même quel que soit le fuseau
        scheduledAt: new Date(`${d.scheduledDate}T12:00:00`).toISOString(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
      toast.success('Intervention planifiée');
      onOpenChange(false);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la planification')),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Planifier une intervention</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
          <div>
            <Label htmlFor="wo-vehicle">Véhicule *</Label>
            <Select
              value={vehicleId}
              onValueChange={(v) => { setValue('vehicleId', v, { shouldValidate: true }); setValue('planId', NO_PLAN); }}
            >
              <SelectTrigger id="wo-vehicle"><SelectValue placeholder="Choisir un véhicule" /></SelectTrigger>
              <SelectContent>
                {vehicles?.data?.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate}</SelectItem>)}
              </SelectContent>
            </Select>
            {errors.vehicleId && <p className="text-xs text-danger mt-1">{errors.vehicleId.message}</p>}
          </div>
          <div>
            <Label htmlFor="wo-desc">Intervention *</Label>
            <Input id="wo-desc" {...register('description')} placeholder="Remplacement des plaquettes de frein" />
            {errors.description && <p className="text-xs text-danger mt-1">{errors.description.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="wo-date">Date prévue *</Label>
              <Input id="wo-date" type="date" {...register('scheduledDate')} />
              {errors.scheduledDate && <p className="text-xs text-danger mt-1">{errors.scheduledDate.message}</p>}
            </div>
            <div>
              <Label htmlFor="wo-plan">Plan préventif lié</Label>
              <Select value={watch('planId')} onValueChange={(v) => setValue('planId', v)} disabled={!vehicleId}>
                <SelectTrigger id="wo-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PLAN}>Aucun (intervention ponctuelle)</SelectItem>
                  {plans?.data?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Lié à un plan préventif, l&apos;ordre de travail terminé recale la prochaine échéance du plan.
          </p>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Planification…' : 'Planifier'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
